import { put, takeLatest, all, select, fork, take, delay } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { isEmpty, pathEq } from 'ramda';
import { ContentRegistryActions, ContentRegistryTypes } from './contentRegistry.redux';
import * as Contentful from '../../shared/services/contentful';
import { ExperienceActions } from '../experience';
import { KioskActions, selectCurrentKioskSlug, selectIsKioskMode } from '../kiosk';
import { MarketActions, MarketTypes } from '../market';
import { recentContentfulUpdateRef } from '../../shared/services/firebase';
import { selectEntities, selectExperienceById, selectExperiencesSlugs } from './contentRegistry.selectors';
import history from '../../shared/utils/history';
import { ROUTES } from '../../routes/routes';
import normalizeData from '../../shared/utils/normalizeData';
import {
  persistContentfulRecentUpdate,
  persistContentEntities,
  rehydrateContentEntities,
  rehydrateContentfulRecentUpdate,
} from '../../shared/services/localStorage';
import { AnalyticsActions } from '../analytics';

function* fetchKiosk({ slug }) {
  const data = yield Contentful.fetchKiosk(slug);
  yield put(ContentRegistryActions.addEntities(normalizeData(data)));
  yield put(KioskActions.setCurrentKioskBySlug(slug));
}

function* fetchMarket({ slug, threshold }) {
  const data = yield Contentful.fetchMarket(slug);
  yield put(ContentRegistryActions.addEntities(normalizeData(data)));
  yield put(MarketActions.setCurrentMarketBySlug(slug));

  yield take(ContentRegistryTypes.FETCH_MARKET_EXPERIENCES_FINISHED);
  yield put(AnalyticsActions.logMarketViewedExperiences(0, threshold - 1));
}

function* fetchAllMarkets() {
  const data = yield Contentful.fetchAllMarkets();
  const normalizedData = normalizeData(data);
  yield put(ContentRegistryActions.fetchAllMarketsFinished());
  yield put(ContentRegistryActions.addEntities(normalizedData));
}

function* fetchMarketExperiences({ marketId }) {
  const data = yield Contentful.fetchExperiencesByMarketId(marketId);
  yield put(ContentRegistryActions.addEntities(normalizeData(data)));
  yield put(ContentRegistryActions.fetchMarketExperiencesFinished(marketId));
}

function* setCurrentMarketId({ id }) {
  yield put(ContentRegistryActions.fetchMarketExperiences(id));
}

function* isExperienceLoaded(slug) {
  const experiencesSlugs = yield select(selectExperiencesSlugs);
  return !!experiencesSlugs[slug];
}

function* fetchExperience({ slug }) {
  const isAlreadyLoaded = yield isExperienceLoaded(slug);
  if (!isAlreadyLoaded) {
    const data = yield Contentful.fetchExperience(slug);
    if (pathEq(['items', 'length'], 0, data)) {
      history.push(ROUTES.notFound);
    } else {
      yield put(ContentRegistryActions.addEntities(normalizeData(data)));
    }
  }
  yield put(ExperienceActions.setCurrentExperienceBySlug(slug));
}

function* fetchExperienceById({ id }) {
  const isAlreadyLoaded = yield select(selectExperienceById(id));
  if (!isAlreadyLoaded) {
    const data = yield Contentful.fetchExperienceById(id);
    yield put(ContentRegistryActions.addEntities(normalizeData(data)));
  }
}

function* fetchAppConfig() {
  const data = yield Contentful.fetchAppConfig();
  yield put(ContentRegistryActions.addEntities(normalizeData(data)));
}

function* fetchWebsiteConfig() {
  const data = yield Contentful.fetchWebsiteConfig();
  yield put(ContentRegistryActions.addEntities(normalizeData(data)));
  yield put(MarketActions.fetchMarketsExperiencesTotal());
}

function* fetchFooterConfig() {
  const data = yield Contentful.fetchFooterConfig();
  yield put(ContentRegistryActions.addEntities(normalizeData(data)));
}

function* rehydrateSuccess() {
  yield put(ContentRegistryActions.startListeningContentUpdate());
}

function* addEntities() {
  const isKioskMode = yield select(selectIsKioskMode);
  if (isKioskMode) {
    yield put(ContentRegistryActions.persist());
  }
}

function* persist() {
  const data = yield select(selectEntities);
  yield persistContentEntities(data);
}

function* rehydrate() {
  const existingRecentUpdate = parseInt(+(yield rehydrateContentfulRecentUpdate()), 10);
  const currentRecentUpdate = (yield recentContentfulUpdateRef.once('value')).val();

  if (currentRecentUpdate !== existingRecentUpdate) {
    yield persistContentfulRecentUpdate(currentRecentUpdate);
  } else {
    const existingContent = yield rehydrateContentEntities();
    if (!isEmpty(existingContent)) {
      yield put(ContentRegistryActions.rehydrateSuccess(existingContent));
      return;
    }
  }

  yield put(ContentRegistryActions.rehydrateSuccess());
}

function* setRecentContentUpdate({ shouldSkipFetching }) {
  if (shouldSkipFetching) {
    return;
  }

  const isKioskMode = yield select(selectIsKioskMode);

  if (!isKioskMode) {
    return;
  }

  const currentKioskSlug = yield select(selectCurrentKioskSlug);
  yield put(ContentRegistryActions.fetchKiosk(currentKioskSlug));
}

const createContentUpdateValueChannel = () =>
  eventChannel(emit => {
    const listener = recentContentfulUpdateRef.on('value', snapshot => setTimeout(() => emit(snapshot.val()), 0));
    return () => recentContentfulUpdateRef.off('value', listener);
  });

function* closeContentUpdateChannelOnNewListenOrStop(channel) {
  while (true) {
    yield take([
      ContentRegistryTypes.START_LISTENING_CONTENT_UPDATE,
      ContentRegistryTypes.STOP_LISTENING_CONTENT_UPDATE,
    ]);
    channel.close();
  }
}

function* setRecentContentUpdateWithDelay(channel, delayValue = 180000, shouldSkipFetching) {
  const recentContentUpdate = yield take(channel);

  yield delay(delayValue);
  yield put(ContentRegistryActions.setRecentContentUpdate(recentContentUpdate, shouldSkipFetching));
}

function* startListeningForContentUpdateValue(dbRef) {
  const channel = createContentUpdateValueChannel(dbRef);

  yield fork(closeContentUpdateChannelOnNewListenOrStop, channel);

  yield setRecentContentUpdateWithDelay(channel, 0, true);

  while (true) {
    yield setRecentContentUpdateWithDelay(channel);
  }
}

function* startListeningContentUpdate() {
  yield fork(startListeningForContentUpdateValue);
}

export default function* contentRegistrySaga() {
  yield all([
    takeLatest(ContentRegistryTypes.FETCH_EXPERIENCE, fetchExperience),
    takeLatest(ContentRegistryTypes.FETCH_EXPERIENCE_BY_ID, fetchExperienceById),
    takeLatest(ContentRegistryTypes.FETCH_KIOSK, fetchKiosk),
    takeLatest(ContentRegistryTypes.FETCH_MARKET, fetchMarket),
    takeLatest(ContentRegistryTypes.FETCH_ALL_MARKETS, fetchAllMarkets),
    takeLatest(ContentRegistryTypes.FETCH_APP_CONFIG, fetchAppConfig),
    takeLatest(ContentRegistryTypes.FETCH_APP_CONFIG, fetchWebsiteConfig),
    takeLatest(ContentRegistryTypes.FETCH_APP_CONFIG, fetchFooterConfig),
    takeLatest(ContentRegistryTypes.FETCH_MARKET_EXPERIENCES, fetchMarketExperiences),
    takeLatest(ContentRegistryTypes.ADD_ENTITIES, addEntities),
    takeLatest(ContentRegistryTypes.PERSIST, persist),
    takeLatest(ContentRegistryTypes.REHYDRATE, rehydrate),
    takeLatest(ContentRegistryTypes.REHYDRATE_SUCCESS, rehydrateSuccess),
    takeLatest(ContentRegistryTypes.SET_RECENT_CONTENT_UPDATE, setRecentContentUpdate),
    takeLatest(ContentRegistryTypes.START_LISTENING_CONTENT_UPDATE, startListeningContentUpdate),
    takeLatest(MarketTypes.SET_CURRENT_MARKET_ID, setCurrentMarketId),
  ]);
}
